import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Badge, Form, Input, Button, Table, Switch, Popconfirm, message,
  Select, DatePicker, Popover, Tag, InputNumber, Radio, Tooltip, Modal, Space, Spin,
} from "antd";
import {
  DeleteOutlined, CloseCircleOutlined, CheckCircleOutlined, SearchOutlined,
  TruckOutlined, PrinterOutlined, DownloadOutlined, SyncOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import { EditDocsSales, PrintedFormDocs } from "src/features/Modal";
import { apiRequests } from "src/shared/api/api";
import {
  ContragentAutocomplete, DebounceSelectFil, OrganizationAutoComplete, WareHousesAutocomplete,
} from "../../shared";
import CreateDocsSalesWrapper from "../../features/Modal/ui/CreateDocsSalesWrapper";
import DocsSalesCalendar from "../KanbanDocsSales/DocsSalesCalendar";

const statusOptions = [
  { label: "Новый заказ",      value: "received"   },
  { label: "В обработке",      value: "processed"  },
  { label: "Сборка начата",    value: "collecting" },
  { label: "Собран",           value: "collected"  },
  { label: "Передан курьеру",  value: "picked"     },
  { label: "Доставлен",        value: "delivered"  },
  { label: "Успешно завершен", value: "success"    },
  { label: "Закрыт",           value: "closed"     },
];

const cdekStatusMap = {
  CREATED:                                  { color: "blue",       text: "Создан" },
  REMOVED:                                  { color: "default",    text: "Удален" },
  RECEIVED_AT_SHIPMENT_WAREHOUSE:           { color: "processing", text: "Принят на склад отправителя" },
  READY_TO_SHIP_AT_SENDING_OFFICE:          { color: "orange",     text: "Выдан на отправку в г. отправителе" },
  READY_FOR_SHIPMENT_IN_TRANSIT_CITY:       { color: "orange",     text: "Выдан на отправку в г. отправителе" },
  RETURNED_TO_SENDER_CITY_WAREHOUSE:        { color: "default",    text: "Возвращен на склад отправителя" },
  PASSED_TO_CARRIER_AT_SENDING_OFFICE:      { color: "orange",     text: "Сдан перевозчику в г. отправителе" },
  SEND_TO_TRANSIT_OFFICE:                   { color: "warning",    text: "Отправлен в г. транзит" },
  MET_AT_TRANSIT_OFFICE:                    { color: "warning",    text: "Встречен в г. транзите" },
  ACCEPTED_AT_TRANSIT_WAREHOUSE:            { color: "processing", text: "Принят на склад транзита" },
  RETURNED_TO_TRANSIT_WAREHOUSE:            { color: "default",    text: "Возвращен на склад транзита" },
  READY_TO_SHIP_IN_TRANSIT_OFFICE:          { color: "orange",     text: "Выдан на отправку в г. транзите" },
  PASSED_TO_CARRIER_AT_TRANSIT_OFFICE:      { color: "orange",     text: "Сдан перевозчику в г. транзите" },
  SENT_TO_SENDER_CITY:                      { color: "warning",    text: "Отправлен в г. отправитель" },
  MET_AT_SENDING_OFFICE:                    { color: "warning",    text: "Встречен в г. отправителе" },
  MET_AT_RECIPIENT_OFFICE:                  { color: "warning",    text: "Встречен в г. получателе" },
  ACCEPTED_AT_RECIPIENT_CITY_WAREHOUSE:     { color: "processing", text: "Принят на склад доставки" },
  ACCEPTED_AT_PICK_UP_POINT:                { color: "processing", text: "Принят на склад до востребования" },
  TAKEN_BY_COURIER:                         { color: "orange",     text: "Выдан на доставку" },
  RETURNED_TO_RECIPIENT_CITY_WAREHOUSE:     { color: "default",    text: "Возвращен на склад доставки" },
  DELIVERED:                                { color: "green",      text: "Вручен" },
  NOT_DELIVERED:                            { color: "red",        text: "Не вручен" },
  ENTERED_TO_OFFICE_TRANSIT_WAREHOUSE:      { color: "processing", text: "Поступил в г. транзита" },
  ENTERED_TO_DELIVERY_WAREHOUSE:            { color: "processing", text: "Поступил на склад доставки" },
  ENTERED_TO_WAREHOUSE_ON_DEMAND:           { color: "processing", text: "Поступил на склад до востребования" },
  IN_CUSTOMS_INTERNATIONAL:                 { color: "warning",    text: "Таможенное оформление в стране отправления" },
  SHIPPED_TO_DESTINATION:                   { color: "warning",    text: "Отправлено в страну назначения" },
  PASSED_TO_TRANSIT_CARRIER:                { color: "orange",     text: "Передано транзитному перевозчику" },
  IN_CUSTOMS_LOCAL:                         { color: "warning",    text: "Таможенное оформление в стране назначения" },
  CUSTOMS_COMPLETE:                         { color: "green",      text: "Таможенное оформление завершено" },
  POSTOMAT_POSTED:                          { color: "processing", text: "Заложен в постамат" },
  POSTOMAT_SEIZED:                          { color: "red",        text: "Изъят из постамата курьером" },
  POSTOMAT_RECEIVED:                        { color: "green",      text: "Изъят из постамата клиентом" },
  READY_FOR_SHIPMENT_IN_SENDER_CITY:        { color: "orange",     text: "Готов к отправке в городе отправителе" },
  TAKEN_BY_TRANSPORTER_FROM_SENDER_CITY:    { color: "orange",     text: "Сдан перевозчику в городе отправителе" },
  SENT_TO_TRANSIT_CITY:                     { color: "warning",    text: "Отправлен в город-транзит" },
  ACCEPTED_IN_TRANSIT_CITY:                 { color: "warning",    text: "Встречен в городе-транзите" },
  TAKEN_BY_TRANSPORTER_FROM_TRANSIT_CITY:   { color: "orange",     text: "Сдан перевозчику в городе-транзите" },
  SENT_TO_RECIPIENT_CITY:                   { color: "warning",    text: "Отправлен в город-получатель" },
  ACCEPTED_IN_RECIPIENT_CITY:               { color: "warning",    text: "Встречен в городе-получателе" },
};

const { RangePicker } = DatePicker;

const SmartPrintCell = ({ record, token, cdekEnabled }) => {
  const regularPrintRef   = useRef(null);
  const [checking,        setChecking]        = useState(false);
  const [hasChecked,      setHasChecked]      = useState(false);
  const [cdekUuid,        setCdekUuid]        = useState(null);
  const [noCdekClick,     setNoCdekClick]     = useState(false);
  const [modalVisible,    setModalVisible]    = useState(false);
  const [waybillLoading,  setWaybillLoading]  = useState(false);
  const [waybillUuid,     setWaybillUuid]     = useState(null);
  const [waybillStatus,   setWaybillStatus]   = useState(null);
  const [waybillUrl,      setWaybillUrl]      = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);

  useEffect(() => {
    if (record.order_uuid) { setCdekUuid(record.order_uuid); setHasChecked(true); }
  }, [record.order_uuid]);

  useEffect(() => {
    if (!noCdekClick) return;
    const btn = regularPrintRef.current?.querySelector("button");
    if (btn) btn.click();
    setNoCdekClick(false);
  }, [noCdekClick]);

  const openCdekWaybill = async (uuid) => {
    setModalVisible(true); setWaybillLoading(true);
    setWaybillUuid(null); setWaybillStatus(null); setWaybillUrl(null);
    try {
      const res = await apiRequests.integrationCdek.printWaybill({ orders: [{ order_uuid: uuid }], copy_count: 2 }, token);
      setWaybillUuid(res.data?.entity?.uuid || null);
      setWaybillStatus(res.data?.entity?.status || res.data?.requests?.[0]?.state || "CREATED");
      setWaybillUrl(res.data?.entity?.url || null);
    } catch (err) {
      console.error(err); message.error("Ошибка создания накладной СДЭК");
    } finally { setWaybillLoading(false); }
  };

  const downloadWaybill = async () => {
    if (!waybillUuid) return;
    setDownloadLoading(true);
    try {
      const res = await apiRequests.integrationCdek.downloadWaybill(waybillUuid, token);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = Object.assign(document.createElement("a"), { href: url });
      link.setAttribute("download", `waybill_${waybillUuid}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      message.success("Накладная скачана");
    } catch (err) {
      console.error(err); message.error("Ошибка скачивания");
    } finally { setDownloadLoading(false); }
  };

  const handleClick = async () => {
    if (!cdekEnabled) { setNoCdekClick(true); return; }
    if (hasChecked) { cdekUuid ? openCdekWaybill(cdekUuid) : setNoCdekClick(true); return; }
    setChecking(true);
    try {
      const res = await apiRequests.integrationCdek.getOrderByDoc(record.id, token);
      const uuid = res.data?.order_uuid || null;
      setCdekUuid(uuid); setHasChecked(true);
      uuid ? openCdekWaybill(uuid) : setNoCdekClick(true);
    } catch (err) {
      if (err.response?.status === 404) { setCdekUuid(null); setHasChecked(true); setNoCdekClick(true); }
      else message.error("Ошибка проверки интеграции СДЭК");
    } finally { setChecking(false); }
  };

  const statusInfo = waybillStatus ? (cdekStatusMap[waybillStatus] || { color: "default", text: waybillStatus }) : null;

  return (
    <>
      <Button icon={<PrinterOutlined />} loading={checking} onClick={handleClick} />
      <div style={{ display: "none" }} ref={regularPrintRef}>
        <PrintedFormDocs token={token} record={record} />
      </div>
      <Modal title="Накладная СДЭК" open={modalVisible} footer={null} onCancel={() => setModalVisible(false)} width={420}>
        {waybillLoading ? (
          <div style={{ textAlign: "center", padding: "32px 0" }}><Spin tip="Создаём накладную..." /></div>
        ) : (
          <Space direction="vertical" style={{ width: "100%" }}>
            {statusInfo && (
              <div style={{ padding: "8px 12px", borderRadius: 6, background: statusInfo.color === "green" ? "#f6ffed" : statusInfo.color === "red" ? "#fff2f0" : "#e6f7ff" }}>
                <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
              </div>
            )}
            {waybillUrl  && <Button block icon={<PrinterOutlined />}  type="primary" onClick={() => window.open(waybillUrl, "_blank")}>Открыть накладную</Button>}
            {waybillUuid && <Button block icon={<DownloadOutlined />}  loading={downloadLoading} onClick={downloadWaybill}>Скачать накладную (PDF)</Button>}
            {!waybillUrl && !waybillUuid && !waybillLoading && <div style={{ color: "#faad14", padding: 8 }}>Накладная не готова. Попробуйте позже.</div>}
          </Space>
        )}
      </Modal>
    </>
  );
};

const EditableContext = React.createContext(null);

const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}><tr {...props} /></EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({ title, editable, children, dataIndex, record, handleSave, ...restProps }) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  useEffect(() => { if (editing) inputRef.current.focus(); }, [editing]);
  const toggleEdit = () => { setEditing(!editing); form.setFieldsValue({ [dataIndex]: record[dataIndex] }); };
  const save = async () => {
    try { const values = await form.validateFields(); toggleEdit(); handleSave({ ...record, ...values }); }
    catch (errInfo) { console.log("Save failed:", errInfo); }
  };
  let childNode = children;
  if (editable) {
    childNode = editing ? (
      <Form.Item style={{ margin: 0 }} name={dataIndex} rules={[{ required: true, message: `${title} обязательно.` }]}>
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      </Form.Item>
    ) : (
      <div className="editable-cell-value-wrap" style={{ paddingRight: 24 }} onClick={toggleEdit}>{children}</div>
    );
  }
  return <td {...restProps}>{childNode}</td>;
};

class DocsSales extends React.Component {
  constructor(props) {
    super(props);
    this.formRef = React.createRef();

    this.baseColumns = [
      {
        title: "Номер", dataIndex: "number", key: "number", editable: false,
        render: (number, record) => {
          let color = "default";
          if (record.color_status) color = record.color_status;
          else if (record.has_contragent && record.has_loyality_card) color = "green";
          else if (record.has_contragent) color = "blue";
          const tagColor = color === "green" ? "success" : color === "blue" ? "processing" : "default";
          const popoverContent = (
            <div>
              <p><strong>Дата:</strong> {record.dated ? new Date(record.dated * 1000).toLocaleDateString() : "—"}</p>
              <p><strong>Сумма:</strong> {record.sum} ₽</p>
              {record.contragent_name && <p><strong>Контрагент:</strong> {record.contragent_name}</p>}
              <p><strong>Оплата:</strong> {record.paid_doc >= (record.sum - record.doc_discount) ? <span style={{ color: "green" }}>Оплачен</span> : <span style={{ color: "red" }}>Не оплачен</span>}</p>
              <p><strong>Товаров:</strong> {record.nomenclature_count}</p>
              {record.paid_loyality > 0 && <p><strong>Бонусами:</strong> {record.paid_loyality} ₽</p>}
            </div>
          );
          return (
            <Popover content={popoverContent} title={`Продажа №${number}`} placement="right">
              <Tag color={tagColor}>{number}</Tag>
            </Popover>
          );
        },
      },
      {
        title: "Приоритет", dataIndex: "priority", key: "priority",
        sorter: (a, b) => a.priority - b.priority, width: 100,
        render: (priority) => {
          let color = "default";
          if (priority >= 8) color = "red";
          else if (priority >= 5) color = "orange";
          return <Tag color={color}>{priority ?? "—"}</Tag>;
        },
      },
      {
        title: "Дата", key: "dated", dataIndex: "dated", editable: false,
        render: (date) => {
          const d = new Date(date * 1000);
          return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()} ${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
        },
      },
      {
        title: "Статус", key: "status", dataIndex: "status", editable: false,
        render: (checked, row) => (
          <Switch style={{ marginLeft: 13 }} checked={checked} onClick={(checked) => this.handleChangeStatus(checked, row)} />
        ),
      },
      {
        title: "Оплачен полностью", key: "full_paid", dataIndex: "full_paid", editable: false,
        render: (_, row) => {
          if (row.paid_doc >= row.sum - row.doc_discount)
            return <font style={{ color: "green" }}><CheckCircleOutlined /> Оплачено</font>;
          return <font style={{ color: "red" }}><CloseCircleOutlined /> Не оплачено</font>;
        },
      },
      { title: "Сумма", key: "sum", dataIndex: "sum", editable: false },
    ];

    if (props.amo_widget === undefined || props.amo_widget !== true) {
      this.baseColumns.push(
        { title: <><span>Скидка</span><br /><span>(на чек)</span></>,   key: "doc_discount",       dataIndex: "doc_discount",       editable: false },
        { title: <><span>Товаров</span><br /><span>(в чеке)</span></>,  key: "nomenclature_count", dataIndex: "nomenclature_count", editable: false },
        { title: "Оплачено",                                            key: "paid_doc",           dataIndex: "paid_doc",           editable: false },
        { title: <><span>Оплачено</span><br /><span>бонусами</span></>, key: "paid_loyality",      dataIndex: "paid_loyality",      editable: false },
        { title: <><span>Оплачено</span><br /><span>рублями</span></>,  key: "paid_rubles",        dataIndex: "paid_rubles",        editable: false },
      );
    }

    this.baseColumns.push({
      title: "Действие", key: "action", width: 160,
      render: (_, record) => {
        if (!this.state.dataSource.length) return "Загрузка...";
        const isCdekMode = this.state.cdekMode;
        return (
          <>
            {isCdekMode ? (
              <Popconfirm
                title="Удалить заказ СДЭК?"
                description="Заказ будет отменён в СДЭК. Документ продажи останется."
                onConfirm={() => this.handleDeleteCdekOrder(record)}
                cancelText="Отмена" okText="Удалить" okButtonProps={{ danger: true }}
              >
                <Button danger icon={<DeleteOutlined />} style={{ marginRight: 6 }} />
              </Popconfirm>
            ) : (
              <Popconfirm title="Подтвердите удаление" onConfirm={() => this.handleDelete(record.id)} cancelText="Отмена" okText="OK">
                <Button icon={<DeleteOutlined />} style={{ marginRight: 6 }} />
              </Popconfirm>
            )}
            <EditDocsSales doc={record} token={this.props.query.token} updateRow={this.updateRow} tags={this.props.tags} />
            <SmartPrintCell record={record} token={this.props.query.token} cdekEnabled={this.state.cdekIntegrationEnabled} />
          </>
        );
      },
    });

    this.state = {
      count:                  this.props.c,
      dataSource:             this.props.ds,
      loading:                true,
      currentPage:            1,
      filterCollapsed:        false,
      viewMode:               "table",
      pickersMeta:            [],
      couriersMeta:           [],
      filters:                this.getInitialFiltersFromUrl(),
      cdekIntegrationEnabled: false,
      cdekMode:               false,
      cdekLiveStatuses:       {},
    };
  }

  getInitialFiltersFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const filters = {};
    params.forEach((value, key) => {
      if (value && key !== "token") {
        if (["tags", "operation", "comment", "order_status"].includes(key)) filters[key] = value.split(",");
        else if (value === "true" || value === "false") filters[key] = value === "true";
        else if (!isNaN(value)) filters[key] = Number(value);
        else if (key.includes("_date") && moment(value, "YYYY-MM-DD", true).isValid()) filters[key] = moment(value, "YYYY-MM-DD");
        else filters[key] = value;
      }
    });
    return filters;
  };

  updateUrlWithFilters = (filters) => {
    const params = new URLSearchParams();
    params.set("token", this.props.query.token);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value)) { if (value.length) params.set(key, value.join(",")); }
        else if (moment.isMoment(value)) params.set(key, value.format("YYYY-MM-DD"));
        else params.set(key, String(value));
      }
    });
    window.history.pushState(null, "", `${window.location.pathname}?${params.toString()}`);
    this.setState({ filters });
  };

  resetFilters = () => {
    this.formRef.current.resetFields();
    this.setState({ filters: {} }, () => {
      window.history.pushState(null, "", `${window.location.pathname}?token=${this.props.query.token}`);
      this.fetch(1, `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/`, {});
    });
    message.success({ content: "Фильтры сброшены", duration: 2 });
  };

  handleViewModeChange = (e) => {
    const newMode = e.target.value;
    this.setState({ viewMode: newMode }, () => {
      if (this.state.cdekMode) {
        this.fetchCdekOrders(this.state.currentPage || 1);
      } else {
        this.fetch(1, `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/`, this.state.filters);
      }
    });
    const searchParams = new URLSearchParams(window.location.search);
    if (newMode === "calendar") searchParams.set("kanban", "true");
    else searchParams.delete("kanban");
    window.history.pushState(null, "", `${window.location.pathname}?${searchParams.toString()}`);
  };

  componentDidMount() {
    window.addEventListener("popstate", this.handlePopState);
    const searchParams = new URLSearchParams(window.location.search);
    const initialFilters = this.state.filters;
    if (searchParams.get("kanban") === "true") this.setState({ viewMode: "calendar" });
    if (Object.keys(initialFilters).length > 0) this.filterHandler(initialFilters);
    else this.fetch(1, `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/`, initialFilters);

    const { websocket } = this.props;
    websocket.onmessage = (msg) => {
      try {
        const data = JSON.parse(msg.data);
        if (data.target === "cdek_status") {
          const { order_uuid, status, doc_sales_id } = data;
          const statusInfo = cdekStatusMap[status];
          if (statusInfo) {
            message.open({
              content: (
                <span>
                  <SyncOutlined spin style={{ color: "#1890ff", marginRight: 6 }} />
                  {"СДЭК: "}
                  <Tag color={statusInfo.color} style={{ margin: 0 }}>{statusInfo.text}</Tag>
                </span>
              ),
              duration: 4,
            });
          }
          this.setState((prev) => {
            const cdekLiveStatuses = { ...prev.cdekLiveStatuses, [order_uuid]: status };
            const dataSource = prev.dataSource.map((row) => {
              const matches = row.order_uuid === order_uuid || (doc_sales_id && row.id === doc_sales_id);
              return matches ? { ...row, cdek_live_status: status } : row;
            });
            return { cdekLiveStatuses, dataSource };
          });
          return;
        }

        if (data.target !== "docs_sales") return;
        if (data.action === "create") {
          this.setState({ loading: true, currentPage: 1 }, () => {
            this.fetch(1, `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/`, this.state.filters);
          });
        } else if (["edit", "update_status", "delete"].includes(data.action)) {
          this.setState({ loading: true }, () => {
            this.fetch(this.state.currentPage || 1, `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/`, this.state.filters);
          });
        }
      } catch (err) { console.error("WS parse error", err); }
    };

    this.checkCdekIntegration();
  }

  checkCdekIntegration = async () => {
    try {
      const response = await apiRequests.integrationCdek.check(this.props.query.token);
      if (response.data?.isAuth) this.setState({ cdekIntegrationEnabled: true });
    } catch (err) { console.error("Ошибка проверки СДЭК:", err); }
  };

  toggleCdekMode = () => {
    const newMode = !this.state.cdekMode;
    this.setState({ cdekMode: newMode, dataSource: [], loading: true }, () => {
      if (newMode) this.fetchCdekOrders(1);
      else this.fetch(1, `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/`, this.state.filters);
    });
  };

  handleDeleteCdekOrder = async (record) => {
    const uuid = record.order_uuid;
    if (!uuid) { message.error("UUID заказа СДЭК не найден"); return; }
    try {
      await apiRequests.integrationCdek.deleteOrder(uuid, this.props.query.token);
      message.success("Заказ СДЭК удалён");
      this.setState((prev) => ({
        dataSource: prev.dataSource.filter((r) => r.order_uuid !== uuid),
        count: Math.max(0, prev.count - 1),
        cdekLiveStatuses: Object.fromEntries(
          Object.entries(prev.cdekLiveStatuses).filter(([k]) => k !== uuid)
        ),
      }));
    } catch (err) {
      console.error(err);
      message.error("Ошибка при удалении заказа СДЭК");
    }
  };

  fetchCdekOrders = async (page = 1) => {
    const limit = 35;
    const offset = (page - 1) * limit;
    this.setState({ dataSource: [], loading: true });
    try {
      const response = await apiRequests.integrationCdek.getOrders(
        { limit, offset, status: this.state.filters?.order_status },
        this.props.query.token
      );
      const items = response.data.items || [];
      const { cdekLiveStatuses } = this.state;
      const dataSource = items.map((item) => {
        const docSales     = item.doc_sales || {};
        const contragent   = docSales.contragent || {};
        const paidLoyality = docSales.loyality_transactions?.reduce((s, t) => s + (t.amount || 0), 0) || 0;
        const paidRubles   = docSales.payments?.reduce((s, p) => s + (p.amount || 0), 0) || 0;
        const totalPaid    = paidLoyality + paidRubles;
        const docDiscount  = docSales.goods?.reduce((s, g) => s + (g.discount || 0), 0) || 0;
        return {
          ...item,
          id:                 docSales.id,
          number:             docSales.number,
          sum:                docSales.sum,
          dated:              docSales.dated,
          comment:            docSales.comment,
          contragent:         docSales.contragent_id,
          contragent_name:    contragent.name,
          goods:              docSales.goods,
          delivery_info:      docSales.delivery_info,
          paid_loyality:      paidLoyality,
          paid_rubles:        paidRubles,
          paid_doc:           totalPaid,
          doc_discount:       docDiscount,
          nomenclature_count: docSales.goods?.length || 0,
          has_contragent:     !!docSales.contragent_id,
          has_loyality_card:  !!(docSales.loyality_transactions?.length),
          color_status:       docSales.loyality_transactions?.length ? "green" : (docSales.contragent_id ? "blue" : "default"),
          cdek_live_status:   cdekLiveStatuses[item.order_uuid] || null,
        };
      });
      this.setState({ dataSource, count: response.data.total, loading: false, currentPage: page });
    } catch (err) {
      console.error("Ошибка загрузки заказов СДЭК:", err);
      message.error("Не удалось загрузить заказы СДЭК");
      this.setState({ loading: false });
    }
  };

  componentWillUnmount() {
    window.removeEventListener("popstate", this.handlePopState);
  }

  handlePopState = () => {
    const newFilters = this.getInitialFiltersFromUrl();
    this.setState({ filters: newFilters }, () => this.filterHandler(newFilters));
  };

  handleChangeStatus = (status, row) => { this.edit_request(row.id, row, { status }); };

  fetch = (page, url = {}, filters = {}) => {
    const limit  = 35;
    const offset = page * 35 - 35;
    let params   = { token: this.props.query.token, limit, offset, ...filters };
    if (this.props.tags !== undefined) params.tags = this.props.tags;

    axios.get(url, { params }).then((response) => {
      const raw     = response.data.result || [];
      const newData = raw.map((rowData) => ({
        created_at: Date.now(), updated_at: Date.now(),
        response_code: 200, response: JSON.stringify(rowData, null, 3),
        ...rowData,
      }));

      const pickersMap  = new Map();
      const couriersMap = new Map();
      raw.forEach((doc) => {
        const p = doc.assigned_picker;
        if (p != null) {
          const id   = typeof p === "object" ? p.id : p;
          const name = typeof p === "object" ? ([p.last_name, p.first_name].filter(Boolean).join(" ") || `ID ${id}`) : `ID ${p}`;
          if (id != null) pickersMap.set(String(id), { label: name, value: id });
        }
        const c = doc.assigned_courier;
        if (c != null) {
          const id   = typeof c === "object" ? c.id : c;
          const name = typeof c === "object" ? ([c.last_name, c.first_name].filter(Boolean).join(" ") || `ID ${id}`) : `ID ${c}`;
          if (id != null) couriersMap.set(String(id), { label: name, value: id });
        }
      });

      if (raw.length > 0) {
        this.setState({
          tagsMeta:       raw.map((d) => d.tags?.split(",")).flat(1).filter((v, i, s) => v && s.indexOf(v) === i).map((v, i) => ({ label: v, value: i })),
          operationsMeta: raw.map((d) => d.operation).filter((v, i, s) => s.indexOf(v) === i).map((v, i) => ({ label: v, value: i })),
          commentsMeta:   raw.map((d) => d.comment).filter((v, i, s) => s.indexOf(v) === i).map((v, i) => ({ label: v, value: i })),
        });
      }

      this.setState({
        count: response.data.count, dataSource: newData, loading: false,
        pickersMeta:  Array.from(pickersMap.values()),
        couriersMeta: Array.from(couriersMap.values()),
      });
    });
  };

  handleDelete = (id) => {
    const dataSource = [...this.state.dataSource];
    const idx        = dataSource.findIndex((item) => item.id === id);
    dataSource.splice(idx, 1);
    this.setState({ dataSource });
    axios.delete(`https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/${id}/`, { params: { token: this.props.query.token } })
      .then(() => message.success("Документ удалён"))
      .catch(() => message.error("Не удалось удалить документ"));
  };

  edit_request = (id, payment, row) => {
    let edit_dict = {};
    for (let item in row) { if (row[item] !== payment[item]) edit_dict[item] = row[item]; }
    edit_dict.id           = id;
    edit_dict.organization = payment.organization;
    if (Object.keys(edit_dict).length !== 0) apiRequests.docs.patchSales(id, [edit_dict]);
    else message.error("Вы не сделали никаких изменений!");
  };

  handleSave = (row) => {
    const newData = [...this.state.dataSource];
    const index   = newData.findIndex((item) => row.id === item.id);
    const item    = newData[index];
    newData.splice(index, 1, { ...item, ...row });
    this.setState({ dataSource: newData });
    this.edit_request(newData.splice(index, 1, { ...item, ...row })[0]);
  };

  convertToApiParams = (values) => {
    const params = { ...values };
    ["dated", "created_at", "updated_at", "delivery_date"].forEach((key) => {
      if (params[key]) {
        params[`${key}_from`] = params[key][0].unix();
        params[`${key}_to`]   = params[key][1].unix();
        delete params[key];
      }
    });
    if (params.sales_manager?.value) params.sales_manager = params.sales_manager.value;
    if (params.created_by?.value)    params.created_by    = params.created_by.value;
    if (params.picker)  { params.picker_id  = params.picker.value  || params.picker;  delete params.picker; }
    if (params.courier) { params.courier_id = params.courier.value || params.courier; delete params.courier; }
    ["has_delivery", "has_picker", "has_courier"].forEach((k) => { if (params[k] === "any") delete params[k]; });
    if (params.order_status && Array.isArray(params.order_status)) params.order_status = params.order_status.join(",");
    if (params.tags      && Array.isArray(params.tags))      params.tags      = params.tags.map((i) => i.label || i.value).join(",");
    if (params.operation && Array.isArray(params.operation)) params.operation = params.operation.map((i) => i.label || i.value).join(",");
    if (params.comment   && Array.isArray(params.comment))   params.comment   = params.comment.map((i) => i.label || i.value).join(",");
    Object.keys(params).forEach((k) => {
      if (params[k] === undefined || params[k] === null || params[k] === "" || (Array.isArray(params[k]) && !params[k].length)) delete params[k];
    });
    return params;
  };

  filterHandler = async (values) => {
    ["has_delivery", "has_picker", "has_courier"].forEach((k) => { if (values[k] === "any") delete values[k]; });
    message.loading({ content: "Поиск..." });
    this.setState({ filters: values }, () => this.updateUrlWithFilters(values));
    const apiParams = this.convertToApiParams(values);
    this.fetch(1, `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/`, apiParams);
    message.destroy();
    message.success({ content: "Поиск завершен", duration: 1 });
  };

  fetchManager = async () =>
    fetch(`https://${process.env.REACT_APP_APP_URL}/api/v1/cashbox_users/?token=${this.props.query.token}`)
      .then((r) => r.json())
      .then((body) => body.result?.map((u) => ({ label: `${u.first_name} ${u.last_name}`, value: u.id })));

  fetchTags = async (name) => {
    if (!name) return;
    return fetch(`https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/?token=${this.props.query.token}&tags=${name}`)
      .then((r) => r.json())
      .then((body) => {
        if (!body.result?.length) return;
        const res = body.result.flatMap((d) => d.tags.split(",")).filter((t) => t.toUpperCase().includes(name.toUpperCase())).map((v, i) => ({ label: v, value: i }));
        this.setState({ tagsMeta: res }); return res;
      });
  };

  fetchOperations = async (name) => {
    if (!name) return;
    return fetch(`https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/?token=${this.props.query.token}&operation=${name}`)
      .then((r) => r.json())
      .then((body) => {
        if (!body.result?.length) return;
        const res = body.result.map((d) => d.operation).filter((v) => v.toUpperCase().includes(name.toUpperCase())).map((v, i) => ({ label: v, value: i }));
        this.setState({ operationsMeta: res }); return res;
      });
  };

  fetchComments = async (name) => {
    if (!name) return;
    return fetch(`https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/?token=${this.props.query.token}&comment=${name}`)
      .then((r) => r.json())
      .then((body) => {
        if (!body.result?.length) return;
        const res = body.result.map((d) => d.comment).filter((v) => v?.toUpperCase().includes(name.toUpperCase())).map((v, i) => ({ label: v, value: i }));
        this.setState({ commentsMeta: res }); return res;
      });
  };

  updateRow = (data, response) => {
    const res     = response || {};
    const resData = res.data ? res.data : data;
    const id      = resData?.id ?? data?.id;
    const existsInState = this.state.dataSource.some((item) => String(item.id) === String(id));
    const isCreate = res.status === 201 || (!existsInState && !!id);
    if (isCreate) {
      this.setState({ loading: true, currentPage: 1 }, () => {
        this.fetch(1, `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/`, this.state.filters);
      });
    } else {
      this.setState({ loading: true }, () => {
        this.fetch(this.state.currentPage || 1, `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/`, this.state.filters);
      });
    }
  };

  findContragent = async (id) =>
    fetch(`https://${process.env.REACT_APP_APP_URL}/api/v1/contragents/${id}/?token=${this.props.query.token}`)
      .then((r) => r.json());

  onSelectCa = (val) => {
    this.findContragent(val).then((res) => {
      this.setState({ current_contragent: res });
      this.formRef.current.setFieldsValue({ contragent: res.id });
    });
  };

  render() {
    const { dataSource, cdekMode, cdekLiveStatuses } = this.state;
    const components = { body: { row: EditableRow, cell: EditableCell } };

    let columnsToRender = [...this.baseColumns];

    if (cdekMode) {
      const cdekStatusColumn = {
        title: "Статус СДЭК",
        key: "cdek_status",
        dataIndex: "status",
        render: (status, record) => {
          const effectiveStatus = record.cdek_live_status || status;
          const statusInfo      = cdekStatusMap[effectiveStatus] || { color: "default", text: effectiveStatus };
          const isLive          = !!record.cdek_live_status;
          const history         = record.status_history || [];
          const historyContent  = history.length > 0 ? (
            <div style={{ maxWidth: 300 }}>
              {history.map((h, i) => (
                <p key={i} style={{ margin: "4px 0", fontSize: 12 }}>
                  {h.date_time ? new Date(h.date_time).toLocaleString("ru-RU") : ""}
                  {" — "}
                  {h.status_name || h.status_code}
                  {h.city ? ` (${h.city})` : ""}
                </p>
              ))}
            </div>
          ) : null;
          return (
            <Tooltip title={historyContent} placement="left">
              <Badge dot={isLive} color="green" offset={[-2, 2]}>
                <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
              </Badge>
            </Tooltip>
          );
        },
      };
      const sumIdx = columnsToRender.findIndex((c) => c.key === "sum");
      if (sumIdx !== -1) columnsToRender.splice(sumIdx + 1, 0, cdekStatusColumn);
      else columnsToRender.push(cdekStatusColumn);
    }

    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, padding: "8px 0", borderBottom: "1px solid #f0f0f0", gap: 16, flexWrap: "nowrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "1 1 auto", minWidth: 0, flexWrap: "nowrap" }}>
            <CreateDocsSalesWrapper
              token={this.props.query.token}
              updateRow={this.updateRow}
              tags={this.props.tags}
              phone={this.props.phone}
              name={this.props.name}
            />
            {this.state.cdekIntegrationEnabled && (
              <Button
                type={cdekMode ? "primary" : "default"}
                icon={<TruckOutlined />}
                onClick={this.toggleCdekMode}
              >
                {cdekMode ? "Все продажи" : "Доставка СДЭК"}
              </Button>
            )}
            <Radio.Group value={this.state.viewMode} onChange={this.handleViewModeChange} optionType="button" style={{ flexShrink: 0 }}>
              <Radio.Button value="table">Таблица</Radio.Button>
              <Radio.Button value="calendar">Календарь</Radio.Button>
            </Radio.Group>
          </div>
          <Button
            icon={<SearchOutlined />}
            onClick={() => this.setState({ filterCollapsed: !this.state.filterCollapsed })}
            type={this.state.filterCollapsed ? "primary" : "default"}
            style={{ flexShrink: 0 }}
          >
            {this.state.filterCollapsed ? "Скрыть фильтры" : "Показать фильтры"}
          </Button>
        </div>

        {this.state.filterCollapsed && (
          <div style={{ marginBottom: 16, padding: 16, border: "1px solid #d9d9d9", borderRadius: 4, backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <Form ref={this.formRef} layout="inline" onFinish={this.filterHandler}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 16, width: "100%" }}>
                <Form.Item name="priority" style={{ margin: 0 }}>
                  <InputNumber placeholder="Приоритет" min={0} max={10} style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item name="order_status" style={{ margin: 0 }}>
                  <Select options={statusOptions} placeholder="Статус заказа" allowClear mode="multiple" style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item name="has_delivery" style={{ margin: 0 }}>
                  <Select placeholder="Доставка" allowClear style={{ width: "100%" }} options={[{ label: "Любая", value: "any" }, { label: "Нужна", value: true }, { label: "Без", value: false }]} />
                </Form.Item>
                <Form.Item name="has_picker" style={{ margin: 0 }}>
                  <Select placeholder="Сборщик" allowClear style={{ width: "100%" }} options={[{ label: "Любой", value: "any" }, { label: "Назначен", value: true }, { label: "Не назначен", value: false }]} />
                </Form.Item>
                <Form.Item name="has_courier" style={{ margin: 0 }}>
                  <Select placeholder="Курьер" allowClear style={{ width: "100%" }} options={[{ label: "Любой", value: "any" }, { label: "Назначен", value: true }, { label: "Не назначен", value: false }]} />
                </Form.Item>
                <Form.Item name="tags" style={{ margin: 0 }}>
                  <Select options={this.state.tagsMeta} placeholder="Теги" allowClear showSearch labelInValue filterOption={false} mode="tags" onSearch={this.fetchTags} style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item name="operation" style={{ margin: 0 }}>
                  <Select options={this.state.operationsMeta} placeholder="Тип операции" allowClear showSearch labelInValue filterOption={false} mode="tags" onSearch={this.fetchOperations} style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item name="comment" style={{ margin: 0 }}>
                  <Select options={this.state.commentsMeta} placeholder="Комментарий" allowClear showSearch labelInValue filterOption={false} mode="tags" onSearch={this.fetchComments} style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item name="contragent" style={{ margin: 0 }}>
                  <ContragentAutocomplete api={`https://${process.env.REACT_APP_APP_URL}/api/v1/`} token={this.props.query.token} placeholder="Контрагент" allowClear width="100%" onSelect={this.onSelectCa} contragent={this.state.current_contragent} />
                </Form.Item>
                <Form.Item name="organization" style={{ margin: 0 }}>
                  <OrganizationAutoComplete api={`https://${process.env.REACT_APP_APP_URL}/api/v1/`} token={this.props.query.token} labelInValue placeholder="Организация" nodefaultvalue="true" allowClear onSelect={(obj) => this.formRef.current.setFieldValue("organization", obj.value)} style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item name="warehouse" style={{ margin: 0 }}>
                  <WareHousesAutocomplete api={`https://${process.env.REACT_APP_APP_URL}/api/v1/`} token={this.props.query.token} labelInValue placeholder="Склад" nodefaultvalue="true" allowClear onSelect={(obj) => this.formRef.current.setFieldValue("warehouse", obj.value)} style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item name="sales_manager" style={{ margin: 0 }}>
                  <DebounceSelectFil placeholder="Менеджер" fetchOptions={this.fetchManager} removeIcon={null} style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item name="created_by" style={{ margin: 0 }}>
                  <DebounceSelectFil placeholder="Создатель" fetchOptions={this.fetchManager} removeIcon={null} style={{ width: "100%" }} />
                </Form.Item>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16, width: "100%", marginTop: 16 }}>
                <Form.Item name="dated"         style={{ margin: 0 }}><RangePicker style={{ width: "100%" }} placeholder={["Начальная дата", "Конечная дата"]} allowClear format="DD.MM.YY" /></Form.Item>
                <Form.Item name="created_at"    style={{ margin: 0 }}><RangePicker style={{ width: "100%" }} placeholder={["Создан от", "Создан до"]}          allowClear format="DD.MM.YY" /></Form.Item>
                <Form.Item name="updated_at"    style={{ margin: 0 }}><RangePicker style={{ width: "100%" }} placeholder={["Обновлен от", "Обновлен до"]}        allowClear format="DD.MM.YY" /></Form.Item>
                <Form.Item name="delivery_date" style={{ margin: 0 }}><RangePicker style={{ width: "100%" }} placeholder={["Доставка от", "Доставка до"]}        allowClear format="DD.MM.YY" /></Form.Item>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16, gap: 8 }}>
                <Button onClick={this.resetFilters}>Сбросить</Button>
                <Button htmlType="submit" icon={<SearchOutlined />} type="primary">Поиск</Button>
              </div>
            </Form>
          </div>
        )}

        {this.state.viewMode === "table" ? (
          <Table
            components={components}
            rowClassName={(record) => {
              if (record.is_deleted) return "disabled-row";
              const live = record.cdek_live_status;
              if (live === "DELIVERED") return "cdek-row-delivered";
              if (live === "NOT_DELIVERED" || live === "FAILED" || live === "CANCELLED") return "cdek-row-failed";
              return "";
            }}
            rowKey={(record) => record.id || record.order_uuid}
            bordered
            loading={this.state.loading}
            dataSource={dataSource}
            columns={columnsToRender}
            scroll={{ x: "max-content" }}
            pagination={{
              total: this.state.count,
              onChange: (page) => {
                if (cdekMode) this.fetchCdekOrders(page);
                else this.setState({ currentPage: page, loading: true }, () => {
                  this.fetch(page, `https://${process.env.REACT_APP_APP_URL}/api/v1/docs_sales/`, this.state.filters);
                });
              },
              pageSize: 35,
              showSizeChanger: false,
              showTotal: (total) => `Всего ${total} записей`,
            }}
          />
        ) : (
          <DocsSalesCalendar
            dataSource={this.state.dataSource}
            token={this.props.query.token}
            updateRow={this.updateRow}
            cdekLiveStatuses={cdekLiveStatuses}
            isCdekMode={cdekMode}
            visibleStatuses={
              Array.isArray(this.state.filters.order_status)
                ? this.state.filters.order_status
                : (this.state.filters.order_status ? this.state.filters.order_status.split(",") : [])
            }
          />
        )}
      </div>
    );
  }
}

export default DocsSales;